import { Component, inject, Input } from '@angular/core';
import { AskADoctorComponent } from "../ask-a-doctor/ask-a-doctor.component";
import { VirtualUrgentCareComponent } from '../virtual-urgent-care/virtual-urgent-care.component';
import { InPersonUrgentCareComponent } from '../in-person-urgent-care/in-person-urgent-care.component';
import { PrimaryCareProviderComponent } from '../primary-care-provider/primary-care-provider.component';
import { NeedHelpDecidingComponent } from '../need-help-deciding/need-help-deciding.component';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { CommonModule } from '@angular/common';
import { GeolocationService } from '../../../../shared/services/geolocation/geolocation.service';
import { MatButtonModule } from '@angular/material/button';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { environment } from '../../../../../environments/environment';
import { GetFhirPatientSearchR4Response } from '../../../../shared/model/GetFhirPatientSearchR4Response';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getLocaleFromUrl } from '../../../../shared/services/translate/locale-utils';
import { PostGetPatientInPersonUrgentCareRequest } from '../../../../shared/model/getcarenow/PostGetPatientInPersonUrgentCareRequest';
import { PostGetPatientVirtualUrgentCareRequest } from '../../../../shared/model/getcarenow/PostGetPatientVirtualUrgentCareRequest';
import { PostGetPatientPrimaryCareProviderRequest } from '../../../../shared/model/getcarenow/PostGetPatientPrimaryCareProviderRequest';
import { PostGetQuickCareVisitRequest } from '../../../../shared/model/getcarenow/PostGetQuickCareVisitRequest';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MychartWebHeaderComponent } from "../../../../shared/components/mychart-web-header/mychart-web-header.component";
import { IdleTimeoutService } from '../../../../shared/services/idletimeout/idle-timeout.service';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';

@Component({
  selector: 'app-get-care-now-mobile',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatProgressSpinnerModule, MatButtonModule, AskADoctorComponent, VirtualUrgentCareComponent, InPersonUrgentCareComponent, PrimaryCareProviderComponent, NeedHelpDecidingComponent],
  templateUrl: './get-care-now.component.html',
  styleUrl: './get-care-now.component.css'
})
export class GetCareNowComponent {
  geolocation = inject(GeolocationService);
  mcAppGateway = inject(MCAppGatewayService);
  auth : AuthService = inject(AuthService);

  locale: string = '';
  isPlus: boolean = false;
  currentLocation: LocationCoordinates | null = null;

  public eptID: string = '';
  public wprID: string = '';
  public fhirID: string = '';
  public age?: number | null = null;
  public gender?: string;
  public patientInfo: GetFhirPatientSearchR4Response | null = null;
  public patientName: string | null = null;
  public proxyName: string | null = null;
  public pcpId: string | null = null;
  public debugText = '';
  public debugURL = '';

  quickCareOptions: Array<any> = [];
  inpersonUrgentCareOptions: Array<any> = [];
  virtualUrgentCareOptions: Array<any> = [];
  primaryCareProviderOptions: Array<any> = [];

  constructor(private translate: TranslateService, private idleTimeoutService: IdleTimeoutService, private monitoringService: MonitoringService) {
    this.locale = getLocaleFromUrl();
    sessionStorage.setItem("locale", this.locale);
    // this.translate.setDefaultLang('en-US'); // Set the default language for translations
    this.translate.use(this.locale);
  }

  async ngOnInit() {
    this.eptID = sessionStorage.getItem("eptID") ?? "";
    this.wprID = sessionStorage.getItem("wprID") ?? "";
    this.fhirID = sessionStorage.getItem("fhirID") ?? "";
    this.age = parseInt(sessionStorage.getItem("age") || '-1', 10);
    if (isNaN(this.age) || this.age < 0) {
      this.age = null;
    }
    this.gender = sessionStorage.getItem("gender") || '';
    this.patientInfo = JSON.parse(sessionStorage.getItem("patientInfo") || '{}') as GetFhirPatientSearchR4Response;  
    this.isPlus = sessionStorage.getItem("isPlus") === 'true';

    const locationPermissionAllowed = localStorage.getItem('locationPermissionAllowed');
    const storedLocation = localStorage.getItem('currentLocation');

    // For MyChart Plus App, we expect location to be passed in via pre-login page and stored in local storage,  
    // so we can attempt to use it immediately without prompting for geolocation on init. 

    // For standard mobile web, we will attempt geolocation on init 
    // but fallback to allowing user to trigger it if it fails or times out.
    if (locationPermissionAllowed === 'false') {
      // User has previously denied location permission, 
      // so we can skip trying to get location and just fetch data without it
      this.getData();
    }
    else if (locationPermissionAllowed === 'true' && storedLocation) {
      try {
        this.currentLocation = JSON.parse(storedLocation) as LocationCoordinates;
      } catch (err) {
        console.error("Failed to parse stored location:", err);
        localStorage.removeItem('currentLocation'); // Clear invalid data
      }

      this.getData(); // Fetch care options immediately, using stored location if available
    }
    else {
      // we can attempt to get location on init
      // Add a timeout to the geolocation request
      const geolocationPromise = this.geolocation.getCurrentLocationAsync();
      const timeoutPromise = new Promise<LocationCoordinates>((_, reject) =>
        setTimeout(() => reject(new Error('Geolocation timeout')), 2000) // 2 seconds timeout
      );

      await Promise.race([geolocationPromise, timeoutPromise])
        .then(location => {
            this.currentLocation = location;
            this.debugText = `Lat: ${location.latitude}, Long: ${location.longitude}`;
        })
        .catch(err => {
          console.error("Geolocation failed:", err);
          this.debugText = 'Geolocation not available' + (err ? `: ${err}` : '');
        })
        .finally(() => {
          this.getData();
        });
    }

    // Start watching for user inactivity
    this.idleTimeoutService.startWatching(true, this.isPlus);

    if (this.isPlus == true) {
      this.monitoringService.logEvent('Login', { feature: 'get-care-now', platform: 'mobile-plus' });
    }
    else if (this.isPlus == false) {
      this.monitoringService.logEvent('Login', { feature: 'get-care-now', platform: 'mobile-standard' });
    }
    this.debugURL = window.location.href;
  }

  async allowLocation() {
    this.geolocation.getCurrentLocation().subscribe(location => {
      this.currentLocation = location;
      this.getData();
    });
  }

  getData() {
    this.getQuickCareOptions();
    this.getVirtualUrgentCareOptions();
    this.getInPersonUrgentCareOptions();
    this.getPrimaryCareProviderOptions();
  }

  getQuickCareOptions() {
    let quickCareRequest = new PostGetQuickCareVisitRequest();
    quickCareRequest.age = this.age || null;
    quickCareRequest.gender = this.gender || null;
    quickCareRequest.language = this.locale || 'en-US';  
    quickCareRequest.maxDisplayCount = 3;
    
    this.mcAppGateway.PostGetQuickCareVisit(quickCareRequest).subscribe({
      next: (response) => {
        console.log("Quick Care options fetched successfully:", response);
        this.quickCareOptions = response || [];
      }
    });
  }
  
  getVirtualUrgentCareOptions() {
    let vucRequest = new PostGetPatientVirtualUrgentCareRequest();
    vucRequest.patientId = this.eptID;
    vucRequest.fhirPatientID = this.fhirID;
    vucRequest.patientAge = this.age ?? 0;
    vucRequest.maxDisplayCount = 3;

    this.mcAppGateway.PostGetPatientVirtualUrgentCare(vucRequest).subscribe({
      next: (response) => {
        console.log("Virtual Urgent Care options fetched successfully:", response);
        this.virtualUrgentCareOptions = response || [];
      }
    });
  }

  getInPersonUrgentCareOptions() {
    let ipucRequest = new PostGetPatientInPersonUrgentCareRequest();
    ipucRequest.patientId = this.eptID;
    ipucRequest.fhirPatientID = this.fhirID;
    ipucRequest.maxDisplayCount = 3;

    if (this.currentLocation != null) {
      ipucRequest.latitude = +(this.currentLocation.latitude);
      ipucRequest.longitude = +(this.currentLocation.longitude);
    }
    this.mcAppGateway.PostGetPatientInPersonUrgentCare(ipucRequest).subscribe({
      next: (response) => {
        console.log("In-Person Urgent Care options fetched successfully:", response);
        this.inpersonUrgentCareOptions = response || [];
        this.inpersonUrgentCareOptions.forEach(element => {
          element.fullAddress = element.providerName + element.address1 + (element.city ? ', ' + element.city : '') + (element.zip ? ' ' + element.zip : '');
        });
      }
    });
  }

  getPrimaryCareProviderOptions() {
    let request = new PostGetPatientPrimaryCareProviderRequest();
    request.patientId = this.eptID;
    request.myChartAccountId = this.wprID ?? "";
    request.patientAge = this.age ?? request.patientAge;
    request.maxDisplayCount = 3;

    if (this.currentLocation != null) {
      request.latitude = +(this.currentLocation.latitude);
      request.longitude = +(this.currentLocation.longitude);
    }
    this.mcAppGateway.PostGetPatientPrimaryCareProvider(request).subscribe({
      next: (response) => {
        console.log("Primary Care Provider options fetched successfully:", response);
        this.primaryCareProviderOptions = response || [];
        this.pcpId = this.primaryCareProviderOptions.find((prov) => prov.specialties == "Primary Care Provider")?.providerId ?? null;
      }
    });
  }
}
